import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateTime } from 'luxon';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { Popover, PopoverModule } from 'primeng/popover';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';

// TODO: Ajustar imports para apontar para libs do monorepo
// import { convertToLocal, dateTimeNow, startOfDay } from '@nomoh-monorepo/shared-util-tools';
// import { PwaOnlineStatusService } from '@nomoh-monorepo/shared-pwa-service';
import {
  AgendaDataService,
  EstabelecimentoDataService,
  BardoContextDataService,
  NichoTermosService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { I18nService } from '@nomoh-monorepo/data-access-shared-i18n';
import { TranslateModule } from '@ngx-translate/core';
import { PermissionValidatorService } from '@nomoh-monorepo/data-access-auth';
import {
  BottomSheetComponent,
  DeviceService,
} from '@nomoh-monorepo/shared-common-ui';
import { IAgendamento, PERMISSIONS } from '@nomoh-monorepo/shared-types';
import { AgendaFiltroCalendarioComponent } from '../agenda-filtro-calendario/agenda-filtro-calendario.component';
import { AgendaFiltroComponent } from '../agenda-filtro/agenda-filtro.component';
import { AgendaListaAgendamentoComponent } from '../agenda-lista-agendamento/agenda-lista-agendamento.component';
import { AgendaModalInformacaoAgendamentoComponent } from '../agenda-modal-informacao-agendamento/agenda-modal-informacao-agendamento.component';
import { AgendaModalNovoAgendamentoComponent } from '../agenda-modal-novo-agendamento/agenda-modal-novo-agendamento.component';
import { AgendaNotificacaoComponent } from '../agenda-notificacao/agenda-notificacao.component';
import { CalendarioSemanalComponent } from '../calendario-semanal/calendario-semanal.component';

// TODO: Service que precisa ser copiado
// import { AgendaCalendarioService } from './service/agenda-calendario.service';

@Component({
  imports: [
    ButtonModule,
    TagModule,
    TooltipModule,
    AgendaListaAgendamentoComponent,
    CommonModule,
    FormsModule,
    AgendaFiltroComponent,
    PopoverModule,
    BottomSheetComponent,
    AgendaNotificacaoComponent,
    CalendarioSemanalComponent,
    AgendaFiltroCalendarioComponent,
    AgendaModalInformacaoAgendamentoComponent,
    AgendaModalNovoAgendamentoComponent,
    TranslateModule,
  ],
  selector: 'lib-agenda-calendario',
  standalone: true,
  templateUrl: './agenda-calendario.component.html',
  styleUrls: ['./agenda-calendario.component.scss'],
  host: {
    'data-tour': 'agenda-calendario',
  },
  providers: [
    ConfirmationService,
  ],
})
export class AgendaCalendarioComponent {
  private readonly timeZonePadrao = 'America/Sao_Paulo';

  //#region Injeção de Dependências
  agendaDataService = inject(AgendaDataService);
  estabelecimentoDataService = inject(EstabelecimentoDataService);
  bardoContextDataService = inject(BardoContextDataService);
  deviceService = inject(DeviceService);
  i18nService = inject(I18nService);
  private permissionValidatorService = inject(PermissionValidatorService);
  private nichoTermosService = inject(NichoTermosService);
  //#endregion

  $termos = this.nichoTermosService.$termos;

  //#region propriedades
  appointments: Appointment[] = [];
  menuVisivel = true;
  @ViewChild('openPopOver') openPopOver?: Popover;
  //#endregion

  //#region signals
  $feriado = signal<string>('');
  $filtroPreenchido = computed(() =>
    this.agendaDataService.$filtroPreenchido() ? 'n-filter-selected' : ''
  );
  $filtroSheetVisible = signal(false);
  $modalInformacaoVisible = signal(false);
  $modalCadastroVisible = signal(false);
  $modalEncaixe = signal(false);
  $podeCriarEncaixe = this.permissionValidatorService.$hasPermission(
    PERMISSIONS.BARDO_AGENDAS_CRIAR_ENCAIXE
  );
  //#endregion

  agendamentoModal: IAgendamento = {} as IAgendamento;

  toggleFiltro(event: Event): void {
    if (this.deviceService.$isMobile()) {
      this.$filtroSheetVisible.set(true);
    } else {
      this.openPopOver?.toggle(event);
    }
  }

  constructor() {
    effect(() => {
      const agendamento = this.agendaDataService.$agendamentoParaModal();
      if (agendamento) {
        this.agendamentoModal = agendamento;
        this.$modalInformacaoVisible.set(true);
        this.agendaDataService.setAgendamentoParaModal(null);
      }
    });

    effect(() => {
      const dataSelecionada = this.agendaDataService.$dataSelecionada();
      const feriados = this.agendaDataService.$feriados();
      const estabelecimento =
        this.estabelecimentoDataService.$estabelecimentoAtual();

      if (!dataSelecionada) {
        this.$feriado.set('');
        return;
      }

      this.agendaDataService.setDesabilitarNovoAgendamento(false);

      const dataSelecionadaLocal =
        this.toCalendarDateTime(dataSelecionada).startOf('day');
      const hojeLocal = DateTime.now()
        .setZone(this.getTimeZoneEstabelecimento())
        .startOf('day');

      if (dataSelecionadaLocal < hojeLocal) {
        this.agendaDataService.setDesabilitarNovoAgendamento(true);
        this.$feriado.set('');
        return;
      }

      // Verificar se o estabelecimento abre no dia da semana selecionado
      // Luxon weekday: 1=Seg...6=Sáb, 7=Dom → diaId: 1=Dom, 2=Seg, ..., 7=Sáb
      if (estabelecimento?.diasFuncionamento?.length) {
        const weekday = dataSelecionadaLocal.weekday;
        const diaId = weekday === 7 ? 1 : weekday + 1;
        const abreNoDia = estabelecimento.diasFuncionamento.some(
          (d) => d.diaId === diaId
        );
        if (!abreNoDia) {
          this.agendaDataService.setDesabilitarNovoAgendamento(true);
          this.$feriado.set('');
          return;
        }
      }

      const dataSelecionadaISO = dataSelecionadaLocal.toISODate();

      if (!dataSelecionadaISO) {
        this.$feriado.set('');
        return;
      }

      const feriadoDoDia = feriados.find((feriado) => {
        if (!feriado?.date) {
          return false;
        }
        const feriadoISO = DateTime.fromISO(feriado.date, {
          zone: 'utc',
        }).toISODate();
        return feriadoISO === dataSelecionadaISO;
      });

      this.$feriado.set(feriadoDoDia?.name ?? '');

      // Verificar feriado: se o estabelecimento não abre em feriados
      if (feriadoDoDia && estabelecimento && !estabelecimento.abreFeriado) {
        this.agendaDataService.setDesabilitarNovoAgendamento(true);
      }
    });
  }

  novoAgendamento(): void {
    this.$modalEncaixe.set(false);
    this.$modalCadastroVisible.set(true);
  }

  novoEncaixe(): void {
    this.$modalEncaixe.set(true);
    this.$modalCadastroVisible.set(true);
  }

  toggleMenu() {
    this.menuVisivel = !this.menuVisivel;
  }

  private toCalendarDateTime(data: Date): DateTime {
    return DateTime.fromObject(
      {
        year: data.getFullYear(),
        month: data.getMonth() + 1,
        day: data.getDate(),
      },
      { zone: this.getTimeZoneEstabelecimento() }
    );
  }

  private getTimeZoneEstabelecimento(): string {
    const timeZone =
      this.estabelecimentoDataService
        .$estabelecimentoAtual()
        ?.timeZone?.trim() || this.timeZonePadrao;
    return DateTime.now().setZone(timeZone).isValid
      ? timeZone
      : this.timeZonePadrao;
  }
}

interface Appointment {
  time: string;
  client: string;
  service: string;
  professional: string;
  phone: string;
  status: string;
}
